"use client";

import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { downloadFile, truncateText, formatDate } from "@/lib/utils";

type SealedItem = {
  id: string;
  user_id: string;
  chat_id: string | null;
  message_id: string | null;
  content: string;
  meta: any;
  created_at: string;
};

const SealedMemory: React.FC = () => {
  const { toast } = useToast();
  const [items, setItems] = useState<SealedItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const fetchItems = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("sealed_items")
      .select("id,user_id,chat_id,message_id,content,meta,created_at")
      .order("created_at", { ascending: false });

    setLoading(false);
    if (error) {
      console.error("[SealedMemory] Failed to fetch sealed items:", error);
      toast({
        title: "Error",
        description: "Failed to load sealed items.",
        variant: "destructive",
      });
      return;
    }
    setItems((data || []) as SealedItem[]);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const text = `${it.content} ${(it.meta ? JSON.stringify(it.meta) : "")}`.toLowerCase();
      return text.includes(q);
    });
  }, [items, query]);

  const toggleSelect = (id: string, checked: boolean) => {
    setSelected((prev) => ({ ...prev, [id]: checked }));
  };

  const selectedItems = useMemo(
    () => filteredItems.filter((it) => selected[it.id]),
    [filteredItems, selected]
  );

  const deleteItem = async (id: string) => {
    const { error } = await supabase.from("sealed_items").delete().eq("id", id);
    if (error) {
      console.error("[SealedMemory] Failed to delete sealed item:", error);
      toast({
        title: "Delete failed",
        description: "Could not delete this sealed item. Please try again.",
        variant: "destructive",
      });
      return;
    }
    setItems((prev) => prev.filter((it) => it.id !== id));
    toast({ title: "Deleted", description: "Sealed item removed." });
  };

  const exportAsJSON = (toExport: SealedItem[]) => {
    const payload = toExport.map((it) => ({
      id: it.id,
      chat_id: it.chat_id,
      message_id: it.message_id,
      created_at: it.created_at,
      meta: it.meta,
      content: it.content,
    }));
    downloadFile(JSON.stringify(payload, null, 2), `EHCO_Sealed_${new Date().toISOString()}.json`, "application/json");
  };

  const exportAsMarkdown = (toExport: SealedItem[]) => {
    const md = toExport
      .map((it) => {
        const metaLines = [
          `- ID: ${it.id}`,
          `- Created: ${formatDate(it.created_at)}`,
          `- Chat: ${it.chat_id || "n/a"}`,
          `- Message: ${it.message_id || "n/a"}`,
          `- Trust Enabled: ${it.meta?.trust_enabled ? "yes" : "no"}`,
          `- Model: ${it.meta?.model || "unknown"}`,
          `- Role: ${it.meta?.role || "assistant"}`,
        ].join("\n");
        return [
          `# Sealed Item`,
          ``,
          metaLines,
          ``,
          `## Content`,
          typeof it.content === "string" ? it.content : JSON.stringify(it.content, null, 2),
          ``,
          `---`,
          ``,
        ].join("\n");
      })
      .join("\n");
    downloadFile(md, `EHCO_Sealed_${new Date().toISOString()}.md`, "text/markdown");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sealed Memory</CardTitle>
        <CardDescription>
          Browse, search, export, and manage your sealed reflections. Everything here is private to you.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-4">
          <Input
            placeholder="Search sealed items..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="md:max-w-sm"
          />
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchItems} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => (selectedItems.length ? exportAsMarkdown(selectedItems) : exportAsMarkdown(filteredItems))}
              title="Export selected (if any) or all as Markdown"
            >
              Export MD
            </Button>
            <Button
              variant="secondary"
              onClick={() => (selectedItems.length ? exportAsJSON(selectedItems) : exportAsJSON(filteredItems))}
              title="Export selected (if any) or all as JSON"
            >
              Export JSON
            </Button>
          </div>
        </div>

        <div className="rounded-md border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Summary</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    No sealed items found.
                  </TableCell>
                </TableRow>
              )}
              {filteredItems.map((it) => (
                <TableRow key={it.id}>
                  <TableCell>
                    <Checkbox
                      checked={!!selected[it.id]}
                      onCheckedChange={(checked) => toggleSelect(it.id, !!checked)}
                      aria-label="Select sealed item"
                    />
                  </TableCell>
                  <TableCell className="max-w-[36rem]">
                    <div className="text-sm">{truncateText(it.content || "", 160)}</div>
                  </TableCell>
                  <TableCell className="text-sm">{it.meta?.model || "unknown"}</TableCell>
                  <TableCell className="text-sm">{it.meta?.role || "assistant"}</TableCell>
                  <TableCell className="text-sm">{formatDate(it.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportAsMarkdown([it])}
                        title="Export this item as Markdown"
                      >
                        Export
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteItem(it.id)}
                        title="Delete this sealed item"
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default SealedMemory;